import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Text, Icon, Select, MultiSelect, Modal } from '@ui-library';
import { ProjectMembersApiService, type ProjectMemberResponse } from '../services/projectMembersApi';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Button } from '../components/Button/Button';
import { Input } from '../components/Input/Input';
import {
  listTickets,
  listTicketIds,
  getTicket as apiGetTicket,
  createTicket as apiCreateTicket,
  updateTicket as apiUpdateTicket,
  deleteTickets as apiDeleteTickets,
  getTicketCounts as apiGetTicketCounts,
  PAGE_SIZE_OPTIONS,
  type ApiAssignTo,
  type PageSize,
  type SortField as ApiSortField,
  type SortDir as ApiSortDir,
  type TicketListParams,
} from '../services/ticketApi';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TicketStatus = 'Open' | 'In Progress' | 'Closed';
export type TicketPriority = 'Low' | 'Medium' | 'High';
export type TicketSource = 'Manual' | 'CAB';
export type TicketWorkType = 'Data Model' | 'Workflow' | 'Screen' | 'Role' | 'Rule' | 'Report' | 'Integration';
export type TicketExpertise = 'Agent' | 'Human' | 'Agent + Human';
export type TicketPlatformType = 'TAP' | 'SN' | 'PA' | 'SF' | 'Web';

export interface Ticket {
  id: string;
  title: string;
  description?: string;
  status: TicketStatus;
  priority: TicketPriority;
  source: TicketSource;
  assignTo?: ApiAssignTo | null;
  dueDate?: string;
  createdDate: string;
  createdBy: string;
  workType?: TicketWorkType;
  cabReference?: string;
  expertise: TicketExpertise;
  platformType: TicketPlatformType;
  projectId: string;
}

type SortField = ApiSortField;
type SortDir = ApiSortDir;



// ─── Status Badge ─────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: TicketStatus }> = ({ status }) => (
  <span className="inline-block px-bg-v2-sm py-bg-v2-xxs rounded-full bg-bg-secondary font-bg-v2-sans font-bg-v2-regular text-bg-v2-xs text-text-secondary whitespace-nowrap">
    {status}
  </span>
);

// ─── Priority badge ───────────────────────────────────────────────────────────

const PriorityBadge: React.FC<{ priority: TicketPriority }> = ({ priority }) => (
  <span className="inline-block px-bg-v2-sm py-bg-v2-xxs rounded-full bg-bg-secondary font-bg-v2-sans font-bg-v2-regular text-bg-v2-xs text-text-secondary whitespace-nowrap">
    {priority}
  </span>
);

// ─── Plain pill (work type, source, platform) ─────────────────────────────────

const Pill: React.FC<{ label: string }> = ({ label }) => (
  <span className="inline-block px-bg-v2-sm py-bg-v2-xxs rounded-full bg-bg-secondary font-bg-v2-sans font-bg-v2-regular text-bg-v2-xs text-text-secondary whitespace-nowrap">
    {label}
  </span>
);

// ─── Sort icon ────────────────────────────────────────────────────────────────

const SortIcon: React.FC<{ field: SortField; active: SortField; dir: SortDir }> = ({ field, active, dir }) => {
  const isActive = active === field;
  return (
    <span className={`inline-flex flex-col leading-none opacity-0 group-hover:opacity-100 transition-opacity ${isActive ? '!opacity-100' : ''}`} style={{ fontSize: '9px', gap: '1px' }}>
      <span className={isActive && dir === 'asc' ? 'text-text-brand-primary' : 'text-text-quaternary'}>▲</span>
      <span className={isActive && dir === 'desc' ? 'text-text-brand-primary' : 'text-text-quaternary'}>▼</span>
    </span>
  );
};

// ─── Ticket Detail Panel ──────────────────────────────────────────────────────

const TicketDetailPanel: React.FC<{
  ticket: Ticket;
  canEdit: boolean;
  isLoadingDetail: boolean;
  onClose: () => void;
  onSave: (updated: Ticket) => void;
  onDelete: (ticket: Ticket) => void;
  assigneeOptions?: AssigneeOption[];
}> = ({ ticket, canEdit, isLoadingDetail, onClose, onSave, onDelete, assigneeOptions = [] }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Ticket>>({});
  const [editFormError, setEditFormError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const startEdit = () => {
    setEditForm({ ...ticket });
    setEditFormError('');
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditFormError('');
  };

  const saveEdit = () => {
    if (!editForm.title?.trim()) { setEditFormError('Title is required.'); return; }
    const updated: Ticket = {
      ...ticket,
      title: editForm.title.trim(),
      description: editForm.description || '',
      status: (editForm.status as TicketStatus) || ticket.status,
      priority: (editForm.priority as TicketPriority) || ticket.priority,
      source: (editForm.source as TicketSource) || ticket.source,
      workType: (editForm.workType as TicketWorkType) || ticket.workType,
      expertise: (editForm.expertise as TicketExpertise) || ticket.expertise,
      platformType: (editForm.platformType as TicketPlatformType) || ticket.platformType,
      assignTo: editForm.assignTo ?? null,
      dueDate: editForm.dueDate || undefined,
      cabReference: editForm.cabReference || undefined,
    };
    onSave(updated);
    setIsEditing(false);
  };

  return (
    <>
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-bg-primary">
      {/* Header */}
      <div className="flex items-center gap-bg-v2-md h-[60px] px-bg-v2-xl border-b border-border-primary flex-shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center w-6 h-6 rounded-bg-v2-md bg-transparent border-none cursor-pointer text-text-tertiary hover:bg-bg-secondary hover:text-text-secondary transition-all duration-200 flex-shrink-0"
        >
          <Icon name={IconName.ArrowLeft} size="sm" inheritColor />
        </button>
        <Text variant="text-sm" weight="semibold" color="secondary">{ticket.id}</Text>
        <div className="ml-auto flex items-center gap-bg-v2-sm">
          {isEditing ? (
            <>
              <Button label="Cancel" variant="outline" size="small" onClick={cancelEdit} />
              <Button label="Save Changes" variant="primary" size="small" onClick={saveEdit} />
            </>
          ) : canEdit ? (
            <>
              <Button
                label={
                  <span className="flex items-center gap-bg-v2-xs">
                    <Icon name={IconName.Edit} size="sm" inheritColor />
                    Edit
                  </span>
                }
                variant="outline"
                size="small"
                onClick={startEdit}
              />
              <Button
                label={
                  <span className="flex items-center gap-bg-v2-xs">
                    <Icon name={IconName.Delete} size="sm" inheritColor />
                    Delete
                  </span>
                }
                variant="outline"
                size="small"
                onClick={() => setShowDeleteConfirm(true)}
                className="!text-text-error-primary !border-border-error hover:!bg-bg-error-primary hover:!text-text-error-primary"
              />
            </>
          ) : null}
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto modern-scrollbar-spaced">
        {isEditing ? (
          <div className="px-bg-v2-3xl py-bg-v2-xl">
            <TicketForm
              form={editForm}
              formError={editFormError}
              onChange={(patch) => setEditForm(f => ({ ...f, ...patch }))}
              onClearError={() => setEditFormError('')}
              assigneeOptions={assigneeOptions}
            />
          </div>
        ) : (
          <div className="px-bg-v2-3xl py-bg-v2-xl flex flex-col gap-bg-v2-xl w-full">

            {/* Title */}
            <Text variant="text-lg" weight="semibold" color="primary">{ticket.title}</Text>

            {/* Quick status row */}
            <div className="flex items-center gap-bg-v2-md flex-wrap">
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
              {ticket.workType && <Pill label={ticket.workType} />}
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-4 gap-x-bg-v2-3xl gap-y-bg-v2-lg">
              <DetailField label="Status">
                <Text variant="text-sm" weight="regular" color="primary">{ticket.status}</Text>
              </DetailField>
              <DetailField label="Priority">
                <Text variant="text-sm" weight="regular" color="primary">{ticket.priority}</Text>
              </DetailField>
              <DetailField label="Work Type">
                <Text variant="text-sm" weight="regular" color={ticket.workType ? 'primary' : 'tertiary'}>{ticket.workType || '—'}</Text>
              </DetailField>
              <DetailField label="Source">
                <Text variant="text-sm" weight="regular" color="primary">{ticket.source}</Text>
              </DetailField>
              <DetailField label="Expertise">
                <Text variant="text-sm" weight="regular" color="primary">{ticket.expertise}</Text>
              </DetailField>
              <DetailField label="Platform">
                <Text variant="text-sm" weight="regular" color="primary">{ticket.platformType}</Text>
              </DetailField>
              <DetailField label="Assigned To">
                {ticket.assignTo
                  ? <Text variant="text-sm" weight="regular" color="primary">{ticket.assignTo.name}</Text>
                  : <Text variant="text-sm" weight="regular" color="tertiary">Unassigned</Text>
                }
              </DetailField>
              <DetailField label="Due Date">
                <Text variant="text-sm" weight="regular" color={ticket.dueDate ? 'primary' : 'tertiary'}>
                  {ticket.dueDate
                    ? new Date(ticket.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—'}
                </Text>
              </DetailField>
              <DetailField label="Created Date">
                <Text variant="text-sm" weight="regular" color="primary">
                  {new Date(ticket.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Text>
              </DetailField>
              <DetailField label="Created By">
                <Text variant="text-sm" weight="regular" color="primary">{ticket.createdBy}</Text>
              </DetailField>
              {ticket.cabReference && (
                <DetailField label="CAB Reference">
                  <div className="flex items-center gap-bg-v2-xs">
                    <Icon name={IconName.Document} size="sm" inheritColor />
                    <Text variant="text-sm" weight="regular" color="primary">{ticket.cabReference}</Text>
                  </div>
                </DetailField>
              )}
            </div>

            <div className="border-t border-border-secondary" />

            {/* Description */}
            <div className="flex flex-col gap-bg-v2-sm">
              <Text variant="text-xs" weight="regular" className="text-text-tertiary uppercase tracking-wide font-bg-v2-sans">
                Description
              </Text>
              {isLoadingDetail ? (
                <div className="flex flex-col gap-bg-v2-sm">
                  <div className="h-3 skeleton-shimmer rounded w-full" />
                  <div className="h-3 skeleton-shimmer rounded w-4/5" />
                  <div className="h-3 skeleton-shimmer rounded w-3/5" />
                </div>
              ) : (
                <Text variant="text-sm" weight="regular" color="secondary" className="whitespace-pre-wrap break-words">
                  {ticket.description || '—'}
                </Text>
              )}
            </div>

          </div>
        )}
      </div>
    </div>

    {/* Delete confirmation */}
    <Modal
      isOpen={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      title="Delete Ticket"
      size="sm"
      onSubmit={() => { setShowDeleteConfirm(false); onDelete(ticket); }}
      submitLabel="Delete"
      submitVariant="outline"
      submitClassName="!text-text-error-primary !border-border-error hover:!bg-bg-error-primary hover:!text-text-error-primary"
      cancelLabel="Cancel"
    >
      <Text color="secondary">
        Are you sure you want to delete <strong>{ticket.id}</strong>? This action cannot be undone.
      </Text>
    </Modal>
    </>
  );
};

const DetailField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-bg-v2-xs">
    <Text variant="text-xs" weight="regular" className="text-text-tertiary uppercase tracking-wide font-bg-v2-sans">
      {label}
    </Text>
    <div>{children}</div>
  </div>
);

// ─── Sidebar nav item — styled to match GroupItem ─────────────────────────────

const SidebarItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  active: boolean;
  count?: number;
  onClick: () => void;
}> = ({ icon, label, active, count, onClick }) => (
  <div
    onClick={onClick}
    className={`flex items-center gap-bg-v2-sm px-bg-v2-md py-bg-v2-sm rounded-bg-v2-sm cursor-pointer transition-colors duration-200
      ${active ? 'bg-bg-brand-primary-alt' : 'hover:bg-bg-brand-primary-alt'}
    `}
  >
    <div className="flex-shrink-0 text-text-tertiary">{icon}</div>
    <div className="flex-1 min-w-0">
      <Text variant="text-sm" weight="semibold" color="secondary" className="truncate block">
        {label}
      </Text>
    </div>
    {count !== undefined && (
      <span className={`px-bg-v2-sm py-bg-v2-xxs rounded-full font-bg-v2-sans font-bg-v2-medium text-bg-v2-xs flex-shrink-0
        ${active ? 'bg-bg-brand-solid text-text-white' : 'bg-bg-secondary text-text-tertiary'}
      `}>
        {count}
      </span>
    )}
  </div>
);

// ─── Sortable column header ───────────────────────────────────────────────────

const ThSortable: React.FC<{
  field: SortField;
  active: SortField;
  dir: SortDir;
  onSort: (f: SortField) => void;
  children: React.ReactNode;
}> = ({ field, active, dir, onSort, children }) => (
  <th
    onClick={() => onSort(field)}
    className="px-bg-v2-lg py-bg-v2-md text-left cursor-pointer select-none group whitespace-nowrap"
  >
    <div className="flex items-center gap-bg-v2-xs">
      <span className="font-bg-v2-sans font-bg-v2-semibold text-bg-v2-sm uppercase tracking-wide text-text-tertiary group-hover:text-text-secondary transition-colors">
        {children}
      </span>
      <SortIcon field={field} active={active} dir={dir} />
    </div>
  </th>
);

// ─── Assignee autocomplete ────────────────────────────────────────────────────

export interface AssigneeOption {
  value: string;
  label: string;
  email: string;
  role: string;
  pending?: boolean;
}

const ROLE_COLORS: Record<string, string> = {
  owner: 'text-text-brand-primary',
  project_collaborator: 'text-text-secondary',
  project_viewer: 'text-text-tertiary',
};

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  project_collaborator: 'Collaborator',
  project_viewer: 'Viewer',
};

const AssigneeCombobox: React.FC<{
  value: ApiAssignTo | null | undefined;
  options: AssigneeOption[];
  onChange: (val: ApiAssignTo | null) => void;
}> = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Match selected option by email (canonical key stored in DB)
  const selected = value?.email ? options.find(o => o.email === value.email) ?? null : null;

  const handleBlur = (e: React.FocusEvent) => {
    if (containerRef.current?.contains(e.relatedTarget as Node)) return;
    setOpen(false);
  };

  const select = (opt: AssigneeOption) => {
    onChange({ name: opt.label, email: opt.email });
    setOpen(false);
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const displayText = value
    ? value.email
      ? `${value.name} (${value.email})`
      : value.name
    : null;

  return (
    <div ref={containerRef} className="relative flex flex-col gap-bg-v2-xs" onBlur={handleBlur}>
      <label className="font-bg-v2-sans font-bg-v2-medium text-bg-v2-sm text-text-secondary">
        Assign To
      </label>

      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-bg-v2-lg py-bg-v2-md border border-border-primary rounded-bg-v2-md bg-bg-primary focus:outline-none focus:border-border-brand transition-colors text-left"
      >
        <span className={`text-bg-v2-sm font-bg-v2-sans font-bg-v2-regular truncate ${displayText ? 'text-text-primary' : 'text-text-placeholder'}`}>
          {displayText ?? 'Select assignee…'}
        </span>
        <span className="flex items-center gap-bg-v2-xs shrink-0 ml-bg-v2-sm">
          {selected && (
            <span
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={e => e.key === 'Enter' && clear(e as unknown as React.MouseEvent)}
              className="text-text-tertiary hover:text-text-secondary"
            >
              <Icon name={IconName.Cross} size="sm" inheritColor />
            </span>
          )}
          <span className={`transition-transform duration-150 text-text-tertiary ${open ? 'rotate-180' : ''}`}>
            <Icon name={IconName.ChevronDown} size="sm" inheritColor />
          </span>
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 mt-[2px] bg-bg-primary border border-border-primary rounded-bg-v2-md container-shadow-sm overflow-hidden max-h-[240px] overflow-y-auto modern-scrollbar">
          {options.length === 0 ? (
            <div className="px-bg-v2-lg py-bg-v2-sm">
              <Text variant="text-sm" color="tertiary">No members found</Text>
            </div>
          ) : (
            options.map(opt => (
              <button
                key={opt.value}
                type="button"
                onMouseDown={e => { e.preventDefault(); select(opt); }}
                className={`w-full flex items-center justify-between px-bg-v2-lg py-bg-v2-sm hover:bg-bg-secondary transition-colors text-left ${opt.email === value?.email ? 'bg-bg-secondary' : ''}`}
              >
                <div className="flex items-center gap-bg-v2-sm min-w-0 truncate">
                  <span className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium text-text-primary truncate">
                    {opt.label}{opt.email ? ` (${opt.email})` : ''}
                  </span>
                  {opt.pending && (
                    <span className="shrink-0 text-bg-v2-xs font-bg-v2-sans font-bg-v2-medium text-text-warning bg-bg-warning-subtle px-bg-v2-sm py-[1px] rounded-full">
                      Pending
                    </span>
                  )}
                </div>
                <span className={`shrink-0 ml-bg-v2-sm text-bg-v2-xs font-bg-v2-sans font-bg-v2-medium ${ROLE_COLORS[opt.role] || 'text-text-tertiary'}`}>
                  {ROLE_LABELS[opt.role] || opt.role}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─── Ticket Form (shared for create & edit) ───────────────────────────────────

const TicketForm: React.FC<{
  form: Partial<Ticket>;
  formError: string;
  onChange: (patch: Partial<Ticket>) => void;
  onClearError: () => void;
  assigneeOptions?: AssigneeOption[];
}> = ({ form, formError, onChange, onClearError, assigneeOptions = [] }) => (
  <div className="flex flex-col gap-bg-v2-lg">
    <Input
      label="Title"
      placeholder="Enter ticket title"
      value={form.title || ''}
      onChange={(e) => { onChange({ title: e.target.value }); onClearError(); }}
      errorText={formError}
      required
    />

    <div className="grid grid-cols-3 gap-bg-v2-lg">
      <Select
        label="Status"
        options={[
          { value: 'Open', label: 'Open' },
          { value: 'In Progress', label: 'In Progress' },
          { value: 'Closed', label: 'Closed' },
        ]}
        value={form.status || 'Open'}
        onChange={(v) => onChange({ status: v as TicketStatus })}
      />
      <Select
        label="Priority"
        options={[
          { value: 'High', label: 'High' },
          { value: 'Medium', label: 'Medium' },
          { value: 'Low', label: 'Low' },
        ]}
        value={form.priority || 'Medium'}
        onChange={(v) => onChange({ priority: v as TicketPriority })}
      />
      <Select
        label="Work Type"
        placeholder="Select work type"
        options={[
          { value: 'Data Model', label: 'Data Model' },
          { value: 'Workflow', label: 'Workflow' },
          { value: 'Screen', label: 'Screen' },
          { value: 'Role', label: 'Role' },
          { value: 'Rule', label: 'Rule' },
          { value: 'Report', label: 'Report' },
          { value: 'Integration', label: 'Integration' },
        ]}
        value={form.workType || ''}
        onChange={(v) => onChange({ workType: v as TicketWorkType })}
      />
    </div>

    <div className="grid grid-cols-2 gap-bg-v2-lg">
      <Select
        label="Expertise"
        placeholder="Select expertise"
        options={[
          { value: 'Agent', label: 'Agent' },
          { value: 'Human', label: 'Human' },
          { value: 'Agent + Human', label: 'Agent + Human' },
        ]}
        value={form.expertise || ''}
        onChange={(v) => onChange({ expertise: v as TicketExpertise })}
      />
    </div>

    <div className="grid grid-cols-2 gap-bg-v2-lg">
      <AssigneeCombobox
        value={form.assignTo}
        options={assigneeOptions}
        onChange={(val) => onChange({ assignTo: val })}
      />
      <Input
        label="Due Date"
        type="date"
        value={form.dueDate || ''}
        onChange={(e) => onChange({ dueDate: e.target.value })}
      />
    </div>

    <Input
      label="CAB Reference"
      placeholder="e.g. CAB-2024-001 (optional)"
      value={form.cabReference || ''}
      onChange={(e) => onChange({ cabReference: e.target.value })}
    />

    <div className="flex flex-col gap-bg-v2-sm">
      <label className="font-bg-v2-sans font-bg-v2-medium text-bg-v2-sm text-text-secondary">Description</label>
      <textarea
        placeholder="Enter ticket description"
        value={form.description || ''}
        onChange={(e) => onChange({ description: e.target.value })}
        rows={5}
        className="w-full px-bg-v2-lg py-bg-v2-md border border-border-primary rounded-bg-v2-md bg-bg-primary text-bg-v2-md font-bg-v2-sans font-bg-v2-regular text-text-primary placeholder:text-text-placeholder focus:outline-none focus:border-border-brand resize-y min-h-[100px]"
      />
    </div>
  </div>
);

// ─── Compact modal footer ─────────────────────────────────────────────────────

const ModalFooter: React.FC<{
  submitLabel: string;
  onCancel: () => void;
  onSubmit: () => void;
}> = ({ submitLabel, onCancel, onSubmit }) => (
  <div className="flex items-center justify-end gap-bg-v2-md pt-bg-v2-lg border-t border-border-secondary mt-bg-v2-lg">
    <Button label="Cancel" variant="outline" size="small" onClick={onCancel} />
    <Button label={submitLabel} variant="primary" size="small" onClick={onSubmit} />
  </div>
);

// ─── Main TicketsPage ─────────────────────────────────────────────────────────

type TicketView = 'all' | 'my';

interface TicketsPageProps {
  permissions?: string[];
  currentUserId?: string;
  projectPlatformType?: string;
  projectOwnerId?: string;
  projectOwnerName?: string;
  projectOwnerEmail?: string;
}

const PLATFORM_TYPE_MAP: Record<string, TicketPlatformType> = {
  TylerAppPlatform: 'TAP',
  ServiceNow: 'SN',
  PowerApps: 'PA',
  Salesforce: 'SF',
  Web: 'Web',
};

const EMPTY_FORM: Partial<Ticket> = {
  status: 'Open',
  priority: 'Medium',
};

const TicketsPage: React.FC<TicketsPageProps> = ({ permissions = [], currentUserId, projectPlatformType, projectOwnerId, projectOwnerName, projectOwnerEmail }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Read initial state from URL ──────────────────────────────────────────
  const initPage      = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const initPageSize  = (PAGE_SIZE_OPTIONS as readonly number[]).includes(parseInt(searchParams.get('pageSize') || '25', 10))
    ? (parseInt(searchParams.get('pageSize') || '25', 10) as PageSize)
    : 25;
  const initSortField = (searchParams.get('sortField') || 'id') as SortField;
  const initSortDir   = (searchParams.get('sortDir') || 'asc') as SortDir;
  const initView      = (searchParams.get('view') || 'all') as TicketView;
  const initSearch    = searchParams.get('search') || '';
  const initStatus    = searchParams.getAll('status');
  const initPriority  = searchParams.getAll('priority');
  const initSource    = searchParams.getAll('source');
  const initWorkType  = searchParams.getAll('workType');

  const [view, setView]                   = useState<TicketView>(initView);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [searchInput, setSearchInput]     = useState(initSearch);
  const [searchQuery, setSearchQuery]     = useState(initSearch);
  const [statusFilter, setStatusFilter]   = useState<string[]>(initStatus);
  const [priorityFilter, setPriorityFilter] = useState<string[]>(initPriority);
  const [sourceFilter, setSourceFilter]   = useState<string[]>(initSource);
  const [workTypeFilter, setWorkTypeFilter] = useState<string[]>(initWorkType);
  const [sortField, setSortField]         = useState<SortField>(initSortField);
  const [sortDir, setSortDir]             = useState<SortDir>(initSortDir);
  const [page, setPage]                   = useState(initPage);
  const [pageSize, setPageSize]           = useState<PageSize>(initPageSize);

  const [showCreateModal, setShowCreateModal]     = useState(false);
  const [tickets, setTickets]                     = useState<Ticket[]>([]);
  const [totalTickets, setTotalTickets]           = useState(0);
  const [totalPages, setTotalPages]               = useState(1);
  const [totalAllTickets, setTotalAllTickets]     = useState(0);
  const [totalMyTickets, setTotalMyTickets]       = useState(0);
  const [isLoadingTickets, setIsLoadingTickets]   = useState(true);
  const [isSubmitting, setIsSubmitting]           = useState(false);
  const [form, setForm]                           = useState<Partial<Ticket>>(EMPTY_FORM);
  const [formError, setFormError]                 = useState('');
  const [selectedIds, setSelectedIds]             = useState<Set<string>>(new Set());
  const [selectedAllPages, setSelectedAllPages]   = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting]               = useState(false);
  const [isLoadingDetail, setIsLoadingDetail]     = useState(false);
  const [assigneeOptions, setAssigneeOptions]     = useState<AssigneeOption[]>([]);

  // If the URL has ?ticket=<id> on initial mount, we need to gate rendering until
  // the detail fetch resolves – otherwise the list flashes briefly before the
  // detail panel takes over. Initialised synchronously so the very first render
  // already shows the loader (no flicker).
  const [isRestoringDetail, setIsRestoringDetail] = useState<boolean>(
    () => Boolean(searchParams.get('ticket'))
  );

  // Track whether we've already restored the selected ticket from the URL on mount
  const restoredTicketRef = useRef(false);

  // Debounce search input → searchQuery
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchInput = (val: string) => {
    setSearchInput(val);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setSearchQuery(val);
      setPage(1);
    }, 300);
  };

  // ── Load assignee options from project members ───────────────────────────
  useEffect(() => {
    if (!projectId) return;
    ProjectMembersApiService.getProjectMembers(projectId, { is_deleted: false })
      .then(({ members }) => {
        const options: AssigneeOption[] = [];

        // Derive owner email: prefer prop, fall back to invited_by on any member record
        // (every member record stores the inviter's email in invited_by)
        const ownerEmailResolved = projectOwnerEmail || members[0]?.invited_by || '';

        // project_owner_name comes back on member records at runtime
        const anyMember = members[0] as ProjectMemberResponse & { project_owner_name?: string };
        const ownerLabel = projectOwnerName || anyMember?.project_owner_name || ownerEmailResolved || 'Project Owner';

        // Always add owner — even when there are no collaborators (members may be empty)
        const ownerEntry: AssigneeOption = {
          value: ownerEmailResolved || projectOwnerId || 'owner',
          label: ownerLabel,
          email: ownerEmailResolved,
          role: 'owner',
        };
        options.push(ownerEntry);

        // All invited members with active or pending invites
        members
          .filter(m =>
            m.role !== 'owner' &&
            (m.invite_status === 'accepted' || m.invite_status === 'pending')
          )
          .forEach(m => {
            options.push({
              value: m.invited_user,
              label: m.invited_user_name || m.invited_user,
              email: m.invited_user,
              role: m.role,
              pending: m.invite_status !== 'accepted',
            });
          });

        setAssigneeOptions(options);
      })
      .catch(() => {
        const fallbackEmail = projectOwnerEmail || '';
        if (fallbackEmail) {
          setAssigneeOptions([{ value: fallbackEmail, label: projectOwnerName || fallbackEmail, email: fallbackEmail, role: 'owner' }]);
        }
      });
  }, [projectId, projectOwnerId, projectOwnerName, projectOwnerEmail]);

  // ── Sync filter/sort/page state → URL ───────────────────────────────────
  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('page', String(page));
      next.set('pageSize', String(pageSize));
      next.set('sortField', sortField);
      next.set('sortDir', sortDir);
      next.set('view', view);
      if (searchQuery) next.set('search', searchQuery); else next.delete('search');
      next.delete('status'); statusFilter.forEach(v => next.append('status', v));
      next.delete('priority'); priorityFilter.forEach(v => next.append('priority', v));
      next.delete('source'); sourceFilter.forEach(v => next.append('source', v));
      next.delete('workType'); workTypeFilter.forEach(v => next.append('workType', v));
      return next;
    }, { replace: true });
  }, [page, pageSize, sortField, sortDir, view, searchQuery, statusFilter, priorityFilter, sourceFilter, workTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch page ───────────────────────────────────────────────────────────
  // The list is only fetched when it is actually visible (i.e. no detail
  // panel is open and we are not restoring a detail from the URL). When the
  // user lands directly on a ticket-detail URL, this avoids a wasted
  // `listTickets` call. The fetch is keyed by the current filter set so we
  // also avoid refetching on detail open/close cycles when nothing changed.
  const lastListKeyRef = useRef<string | null>(null);
  const listKey = useMemo(() => JSON.stringify({
    projectId, page, pageSize, sortField, sortDir, view, searchQuery,
    statusFilter, priorityFilter, sourceFilter, workTypeFilter,
  }), [projectId, page, pageSize, sortField, sortDir, view, searchQuery, statusFilter, priorityFilter, sourceFilter, workTypeFilter]);

  const fetchTickets = useCallback(async () => {
    if (!projectId) return;
    try {
      setIsLoadingTickets(true);
      const params: TicketListParams = {
        page,
        pageSize,
        sortField,
        sortDir,
        view,
        search: searchQuery || undefined,
        status: statusFilter.length ? statusFilter : undefined,
        priority: priorityFilter.length ? priorityFilter : undefined,
        source: sourceFilter.length ? sourceFilter : undefined,
        workType: workTypeFilter.length ? workTypeFilter : undefined,
      };
      const result = await listTickets(projectId, params);
      setTickets(result.tickets);
      setTotalTickets(result.total);
      setTotalPages(result.totalPages);
      lastListKeyRef.current = listKey;
    } catch (err) {
      console.error('Failed to load tickets:', err);
    } finally {
      setIsLoadingTickets(false);
    }
  }, [projectId, page, pageSize, sortField, sortDir, view, searchQuery, statusFilter, priorityFilter, sourceFilter, workTypeFilter, listKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Skip while the detail is open or being restored — list is hidden anyway.
    if (selectedTicket || isRestoringDetail) return;
    // Skip if we already loaded this exact filter set.
    if (lastListKeyRef.current === listKey) return;
    fetchTickets();
  }, [fetchTickets, selectedTicket, isRestoringDetail, listKey]);

  // Fetch sidebar counts via a dedicated count-only endpoint — single round
  // trip, no list rows fetched. Independent of current filters.
  const fetchCounts = useCallback(async () => {
    if (!projectId) return;
    try {
      const counts = await apiGetTicketCounts(projectId);
      setTotalAllTickets(counts.all);
      setTotalMyTickets(currentUserId ? counts.my : 0);
    } catch { /* ignore */ }
  }, [projectId, currentUserId]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  // ── Restore selected ticket from URL on mount ────────────────────────────
  // Runs in parallel with the list fetch so the detail panel appears as soon
  // as the ticket payload resolves. While in flight, render is gated by
  // `isRestoringDetail` so the user never sees the list flash before detail.
  // NOTE: the de-dupe guard via `restoredTicketRef` is intentionally placed
  // around the in-flight promise (not as an early-return) so that React 18
  // Strict Mode's double-invocation of effects does not leave the spinner
  // stuck forever (the second invocation would otherwise no-op without
  // clearing `isRestoringDetail`).
  useEffect(() => {
    if (!projectId) return;
    const ticketIdFromUrl = searchParams.get('ticket');
    if (!ticketIdFromUrl) {
      setIsRestoringDetail(false);
      return;
    }
    if (restoredTicketRef.current) return;
    restoredTicketRef.current = true;

    setIsLoadingDetail(true);
    apiGetTicket(projectId, ticketIdFromUrl)
      .then(full => setSelectedTicket(full))
      .catch(() => {
        setSearchParams(prev => {
          const n = new URLSearchParams(prev);
          n.delete('ticket');
          return n;
        }, { replace: true });
      })
      .finally(() => {
        setIsLoadingDetail(false);
        setIsRestoringDetail(false);
      });
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ticket selection (with URL sync + lazy description fetch) ────────────
  const selectTicket = useCallback((ticket: Ticket | null) => {
    setSelectedTicket(ticket);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (ticket) { next.set('ticket', ticket.id); } else { next.delete('ticket'); }
      return next;
    }, { replace: true });

    if (ticket && projectId) {
      setIsLoadingDetail(true);
      apiGetTicket(projectId, ticket.id)
        .then(full => setSelectedTicket(full))
        .catch(err => console.error('Failed to load ticket detail:', err))
        .finally(() => setIsLoadingDetail(false));
    }
  }, [setSearchParams, projectId]);

  const canEdit = permissions.length === 0 || permissions.includes('update_project_permission');

  const patchForm = (patch: Partial<Ticket>) => setForm(f => ({ ...f, ...patch }));

  // Reset to page 1 whenever any filter/sort changes
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const applyFilter = (setter: React.Dispatch<React.SetStateAction<string[]>>) =>
    (val: string[]) => { setter(val); setPage(1); };

  const openCreateModal = () => {
    setForm(EMPTY_FORM);
    setFormError('');
    setShowCreateModal(true);
  };

  const handleCreateTicket = async () => {
    if (!form.title?.trim()) { setFormError('Title is required.'); return; }
    if (!projectId) return;
    try {
      setIsSubmitting(true);
      await apiCreateTicket(projectId, {
        title: form.title.trim(),
        description: form.description || '',
        status: (form.status as TicketStatus) || 'Open',
        priority: (form.priority as TicketPriority) || 'Medium',
        source: 'Manual',
        work_type: form.workType as TicketWorkType | undefined,
        expertise: form.expertise as TicketExpertise,
        platform_type: PLATFORM_TYPE_MAP[projectPlatformType || ''] || 'TAP',
        assign_to: form.assignTo ?? undefined,
        due_date: form.dueDate || undefined,
        cab_reference: form.cabReference || undefined,
      });
      setShowCreateModal(false);
      setPage(1);
      fetchTickets();
      fetchCounts();
    } catch (err) {
      console.error('Failed to create ticket:', err);
      setFormError('Failed to create ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveTicket = async (updated: Ticket) => {
    if (!projectId) return;
    try {
      const saved = await apiUpdateTicket(projectId, updated.id, {
        title: updated.title,
        description: updated.description,
        status: updated.status,
        priority: updated.priority,
        source: updated.source,
        work_type: updated.workType,
        expertise: updated.expertise,
        platform_type: updated.platformType,
        assign_to: updated.assignTo ?? null,
        due_date: updated.dueDate,
        cab_reference: updated.cabReference,
      });
      setTickets(prev => prev.map(t => t.id === saved.id ? { ...t, ...saved } : t));
      selectTicket(saved);
    } catch (err) {
      console.error('Failed to update ticket:', err);
    }
  };

  const handleDeleteTicket = async (ticket: Ticket) => {
    if (!projectId) return;
    try {
      await apiDeleteTickets(projectId, ticket.id);
      if (selectedTicket?.id === ticket.id) selectTicket(null);
      fetchTickets();
      fetchCounts();
    } catch (err) {
      console.error('Failed to delete ticket:', err);
    }
  };

  const handleBulkDelete = async () => {
    if (!projectId) return;
    setIsDeleting(true);
    try {
      let idsToDelete: string[];
      if (selectedAllPages) {
        idsToDelete = await listTicketIds(projectId, {
          sortField, sortDir, view,
          search: searchQuery || undefined,
          status: statusFilter.length ? statusFilter : undefined,
          priority: priorityFilter.length ? priorityFilter : undefined,
          source: sourceFilter.length ? sourceFilter : undefined,
          workType: workTypeFilter.length ? workTypeFilter : undefined,
        });
      } else {
        idsToDelete = [...selectedIds];
      }
      if (idsToDelete.length === 0) return;
      await apiDeleteTickets(projectId, idsToDelete);
      if (selectedTicket && idsToDelete.includes(selectedTicket.id)) selectTicket(null);
      setSelectedIds(new Set());
      setSelectedAllPages(false);
      fetchTickets();
      fetchCounts();
    } catch (err) {
      console.error('Failed to delete tickets:', err);
    } finally {
      setIsDeleting(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  const toggleSelectAll = () => {
    const allSelected = tickets.every(t => selectedIds.has(t.id));
    if (allSelected) {
      setSelectedIds(prev => { const next = new Set(prev); tickets.forEach(t => next.delete(t.id)); return next; });
      setSelectedAllPages(false);
    } else {
      setSelectedIds(prev => new Set([...prev, ...tickets.map(t => t.id)]));
      // Don't auto-set selectedAllPages — user must explicitly click the banner
    }
  };

  const selectAcrossAllPages = async () => {
    if (!projectId) return;
    setSelectedAllPages(true);
  };

  const clearAllSelection = () => {
    setSelectedIds(new Set());
    setSelectedAllPages(false);
  };

  const toggleSelectOne = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const statusOptions   = [{ value: 'Open', label: 'Open' }, { value: 'In Progress', label: 'In Progress' }, { value: 'Closed', label: 'Closed' }];
  const priorityOptions = [{ value: 'High', label: 'High' }, { value: 'Medium', label: 'Medium' }, { value: 'Low', label: 'Low' }];
  const sourceOptions   = [{ value: 'Manual', label: 'Manual' }, { value: 'CAB', label: 'CAB' }];
  const workTypeOptions = [
    { value: 'Data Model', label: 'Data Model' }, { value: 'Workflow', label: 'Workflow' },
    { value: 'Screen', label: 'Screen' }, { value: 'Role', label: 'Role' },
    { value: 'Rule', label: 'Rule' }, { value: 'Report', label: 'Report' },
    { value: 'Integration', label: 'Integration' },
  ];

  const activeFilterChips: { group: string; label: string; onRemove: () => void }[] = [
    ...statusFilter.map(v => ({ group: 'Status', label: v, onRemove: () => applyFilter(setStatusFilter)(statusFilter.filter(x => x !== v)) })),
    ...priorityFilter.map(v => ({ group: 'Priority', label: v, onRemove: () => applyFilter(setPriorityFilter)(priorityFilter.filter(x => x !== v)) })),
    ...sourceFilter.map(v => ({ group: 'Source', label: v, onRemove: () => applyFilter(setSourceFilter)(sourceFilter.filter(x => x !== v)) })),
    ...workTypeFilter.map(v => ({ group: 'Work Type', label: v, onRemove: () => applyFilter(setWorkTypeFilter)(workTypeFilter.filter(x => x !== v)) })),
  ];

  const hasActiveFilters = activeFilterChips.length > 0 || searchQuery.trim().length > 0;

  const clearAllFilters = () => {
    setStatusFilter([]); setPriorityFilter([]); setSourceFilter([]); setWorkTypeFilter([]);
    setSearchQuery(''); setSearchInput(''); setPage(1);
  };

  // ── Pagination helpers ───────────────────────────────────────────────────
  const startItem = totalTickets === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem   = Math.min(page * pageSize, totalTickets);

  return (
    <>
    <div className="flex h-full gap-bg-v2-xl overflow-hidden w-full">

      {/* ── Views Panel ── */}
      <div className="w-[280px] flex-shrink-0 flex flex-col bg-bg-primary rounded-bg-v2-lg container-shadow-sm overflow-hidden">
        <div className="flex items-center h-[60px] px-bg-v2-xl border-b border-border-secondary flex-shrink-0">
          <Text variant="text-sm" weight="semibold" color="primary">Views</Text>
        </div>
        <div className="flex-1 px-bg-v2-md pt-bg-v2-md flex flex-col gap-[2px]">
          <SidebarItem
            icon={<Icon name={IconName.Tickets} size="sm" inheritColor />}
            label="All Tickets"
            active={view === 'all'}
            count={totalAllTickets}
            onClick={() => { setView('all'); setPage(1); selectTicket(null); }}
          />
        </div>
      </div>

      {/* ── Main Content Panel ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden bg-bg-primary rounded-bg-v2-lg container-shadow-sm">

        {/* While restoring a ticket detail from the URL on mount, show a single
            centered loader so the list never flashes before the detail panel. */}
        {isRestoringDetail && (
          <div className="flex flex-1 items-center justify-center">
            <div className="w-6 h-6 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin" />
          </div>
        )}

        {/* Ticket list — hidden when detail is open or while restoring from URL */}
        <div className={`flex flex-col flex-1 min-h-0 overflow-hidden ${selectedTicket || isRestoringDetail ? 'hidden' : ''}`}>

          {/* Toolbar */}
          <div className="flex items-center h-[60px] px-bg-v2-xl border-b border-border-secondary flex-shrink-0 gap-bg-v2-md">
            <Text variant="text-sm" weight="semibold" color="primary" as="span">
              All Tickets
              <Text variant="text-sm" weight="regular" color="tertiary" as="span" className="ml-bg-v2-xs">
                ({totalTickets})
              </Text>
            </Text>

            {selectedIds.size > 0 && canEdit && (
              <Button
                label={
                  <span className="flex items-center gap-bg-v2-xs">
                    <Icon name={IconName.Delete} size="sm" inheritColor />
                    Delete selected ({selectedAllPages ? totalTickets : selectedIds.size})
                  </span>
                }
                variant="outline"
                size="small"
                onClick={() => setShowBulkDeleteConfirm(true)}
                className="!text-text-error-primary !border-border-error hover:!bg-bg-error-primary hover:!text-text-error-primary"
              />
            )}

            <div className="flex items-center gap-bg-v2-sm ml-auto flex-wrap">
              <div className="w-[200px]">
                <Input
                  placeholder="Search tickets..."
                  value={searchInput}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  startIcon={<Icon name={IconName.Search} size="sm" inheritColor />}
                  className="!border !border-border-primary"
                />
              </div>
              <div className="w-[140px]">
                <MultiSelect options={statusOptions} value={statusFilter} onChange={applyFilter(setStatusFilter)} placeholder="Status" allSelectedLabel="All Status" selectAllLabel="All Status" />
              </div>
              <div className="w-[140px]">
                <MultiSelect options={priorityOptions} value={priorityFilter} onChange={applyFilter(setPriorityFilter)} placeholder="Priority" allSelectedLabel="All Priority" selectAllLabel="All Priority" />
              </div>
              <div className="w-[130px]">
                <MultiSelect options={sourceOptions} value={sourceFilter} onChange={applyFilter(setSourceFilter)} placeholder="Source" allSelectedLabel="All Source" selectAllLabel="All Source" />
              </div>
              <div className="w-[150px]">
                <MultiSelect options={workTypeOptions} value={workTypeFilter} onChange={applyFilter(setWorkTypeFilter)} placeholder="Work Type" allSelectedLabel="All Work Types" selectAllLabel="All Work Types" />
              </div>
              {canEdit && (
                <Button
                  label={
                    <span className="flex items-center gap-bg-v2-xs">
                      <Icon name={IconName.Plus} size="sm" inheritColor />
                      Create Ticket
                    </span>
                  }
                  variant="primary"
                  size="small"
                  onClick={openCreateModal}
                />
              )}
            </div>
          </div>

          {/* Active filters chip row */}
          {activeFilterChips.length > 0 && (
            <div className="flex items-center gap-bg-v2-sm px-bg-v2-xl py-bg-v2-sm border-b border-border-secondary bg-bg-secondary-alt flex-shrink-0 flex-wrap">
              <Text variant="text-xs" weight="semibold" color="tertiary" className="uppercase tracking-wide">
                Filters
              </Text>
              {activeFilterChips.map((chip) => (
                <span
                  key={`${chip.group}-${chip.label}`}
                  className="inline-flex items-center gap-bg-v2-xs pl-bg-v2-sm pr-bg-v2-xs py-bg-v2-xxs rounded-full bg-bg-primary border border-border-primary text-bg-v2-xs font-bg-v2-sans"
                >
                  <span className="text-text-tertiary font-bg-v2-medium">{chip.group}:</span>
                  <span className="text-text-secondary font-bg-v2-medium">{chip.label}</span>
                  <button
                    type="button"
                    onClick={chip.onRemove}
                    aria-label={`Remove ${chip.group} ${chip.label} filter`}
                    className="flex items-center justify-center w-4 h-4 rounded-full text-text-tertiary hover:bg-bg-secondary hover:text-text-secondary transition-colors"
                  >
                    <Icon name={IconName.Cross} size="sm" inheritColor />
                  </button>
                </span>
              ))}
              <button
                type="button"
                onClick={clearAllFilters}
                className="ml-bg-v2-xs px-bg-v2-sm py-bg-v2-xxs text-bg-v2-xs font-bg-v2-sans font-bg-v2-semibold text-text-brand-primary hover:underline rounded-bg-v2-sm"
              >
                Clear all
              </button>
            </div>
          )}

          {/* ── Cross-page select-all banner ── */}
          {canEdit && tickets.length > 0 && tickets.every(t => selectedIds.has(t.id)) && (
            <div className="flex items-center justify-center gap-bg-v2-sm px-bg-v2-xl py-bg-v2-sm border-b border-border-secondary bg-bg-brand-primary-alt flex-shrink-0">
              {selectedAllPages ? (
                <>
                  <Text variant="text-sm" weight="medium" color="primary" as="span">
                    All <strong>{totalTickets}</strong> tickets are selected.
                  </Text>
                  <button
                    type="button"
                    onClick={clearAllSelection}
                    className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-semibold text-text-brand-primary hover:underline"
                  >
                    Clear selection
                  </button>
                </>
              ) : (
                <>
                  <Text variant="text-sm" weight="medium" color="primary" as="span">
                    All <strong>{tickets.length}</strong> tickets on this page are selected.
                  </Text>
                  {totalTickets > tickets.length && (
                    <button
                      type="button"
                      onClick={selectAcrossAllPages}
                      className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-semibold text-text-brand-primary hover:underline"
                    >
                      Select all {totalTickets} tickets
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Table */}
          <div className="flex-1 overflow-y-auto modern-scrollbar-spaced relative">
            {isLoadingTickets && (
              <div className="absolute inset-0 bg-bg-primary/60 flex items-center justify-center z-20">
                <div className="w-6 h-6 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin" />
              </div>
            )}
            {!isLoadingTickets && tickets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-bg-v2-md">
                <Icon name={IconName.Tickets} size="xl" inheritColor className="!text-text-tertiary" />
                <Text variant="text-sm" color="tertiary">
                  {hasActiveFilters ? 'No tickets match your filters' : 'No tickets found'}
                </Text>
                {hasActiveFilters && (
                  <Button
                    label={<span className="flex items-center gap-bg-v2-xs"><Icon name={IconName.Cross} size="sm" inheritColor />Clear all filters</span>}
                    variant="outline"
                    size="small"
                    onClick={clearAllFilters}
                  />
                )}
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-bg-secondary border-b border-border-secondary">
                    {canEdit && (
                      <th className="px-bg-v2-lg py-bg-v2-md w-10">
                        <input
                          type="checkbox"
                          aria-label="Select all"
                          checked={tickets.length > 0 && tickets.every(t => selectedIds.has(t.id))}
                          ref={el => {
                            if (el) el.indeterminate = tickets.some(t => selectedIds.has(t.id)) && !tickets.every(t => selectedIds.has(t.id));
                          }}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 rounded cursor-pointer accent-bg-brand-solid"
                        />
                      </th>
                    )}
                    <ThSortable field="id" active={sortField} dir={sortDir} onSort={handleSort}>ID</ThSortable>
                    <ThSortable field="title" active={sortField} dir={sortDir} onSort={handleSort}>Title</ThSortable>
                    <ThSortable field="status" active={sortField} dir={sortDir} onSort={handleSort}>Status</ThSortable>
                    <ThSortable field="priority" active={sortField} dir={sortDir} onSort={handleSort}>Priority</ThSortable>
                    <ThSortable field="work_type" active={sortField} dir={sortDir} onSort={handleSort}>Work Type</ThSortable>
                    <ThSortable field="source" active={sortField} dir={sortDir} onSort={handleSort}>Source</ThSortable>
                    <ThSortable field="assign_to" active={sortField} dir={sortDir} onSort={handleSort}>Assigned To</ThSortable>
                    <ThSortable field="created_date" active={sortField} dir={sortDir} onSort={handleSort}>Created Date</ThSortable>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      onClick={() => selectTicket(selectedTicket?.id === ticket.id ? null : ticket)}
                      className={`
                        border-b border-border-secondary cursor-pointer transition-colors duration-150
                        ${selectedIds.has(ticket.id) ? 'bg-bg-brand-primary-alt' : selectedTicket?.id === ticket.id ? 'bg-bg-brand-primary-alt' : 'hover:bg-bg-secondary'}
                      `}
                    >
                      {canEdit && (
                        <td className="px-bg-v2-lg py-bg-v2-md w-10" onClick={e => toggleSelectOne(ticket.id, e)}>
                          <input type="checkbox" checked={selectedIds.has(ticket.id)} onChange={() => {}} className="w-4 h-4 rounded cursor-pointer accent-bg-brand-solid" />
                        </td>
                      )}
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        <Text variant="text-sm" weight="regular" color="secondary" as="span">{ticket.id}</Text>
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md" style={{ maxWidth: 260 }}>
                        <Text variant="text-sm" weight="regular" color="primary" as="span" className="truncate block">{ticket.title}</Text>
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        <Text variant="text-sm" color="secondary" as="span">{ticket.status}</Text>
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        <Text variant="text-sm" color="secondary" as="span">{ticket.priority}</Text>
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        <Text variant="text-sm" color={ticket.workType ? 'secondary' : 'tertiary'} as="span">{ticket.workType || '—'}</Text>
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        <Text variant="text-sm" color="secondary" as="span">{ticket.source}</Text>
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        {ticket.assignTo
                          ? <Text variant="text-sm" color="secondary" as="span">{ticket.assignTo.name}</Text>
                          : <Text variant="text-sm" color="tertiary" as="span">—</Text>
                        }
                      </td>
                      <td className="px-bg-v2-lg py-bg-v2-md whitespace-nowrap">
                        <Text variant="text-sm" color="secondary" as="span">
                          {new Date(ticket.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Text>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Pagination bar ── */}
          {totalTickets > 0 && (
            <div className="flex items-center justify-between px-bg-v2-xl py-bg-v2-md border-t border-border-secondary flex-shrink-0 bg-bg-primary gap-bg-v2-lg flex-wrap">

              {/* Left: result count + page-size picker */}
              <div className="flex items-center gap-bg-v2-lg">
                <Text variant="text-sm" weight="regular" color="tertiary">
                  <span className="text-text-secondary font-bg-v2-medium">{startItem}–{endItem}</span>
                  {' '}of{' '}
                  <span className="text-text-secondary font-bg-v2-medium">{totalTickets}</span>
                  {' '}results
                </Text>

                <div className="w-px h-4 bg-border-secondary flex-shrink-0" />

                <div className="flex items-center gap-bg-v2-sm">
                  <Text variant="text-sm" weight="regular" color="tertiary">Rows per page:</Text>
                  <div className="flex items-center gap-bg-v2-xs">
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => { setPageSize(size); setPage(1); }}
                        className={`min-w-[40px] h-8 px-bg-v2-sm rounded-bg-v2-md text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium border transition-colors
                          ${pageSize === size
                            ? 'bg-bg-brand-solid text-text-white border-transparent'
                            : 'bg-bg-primary text-text-secondary border-border-primary hover:bg-bg-secondary hover:border-border-secondary'
                          }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: prev / page numbers / next */}
              <div className="flex items-center gap-bg-v2-xs">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  className="flex items-center gap-bg-v2-xs h-8 px-bg-v2-sm rounded-bg-v2-md text-text-tertiary hover:text-text-secondary hover:bg-bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Icon name={IconName.ArrowLeft} size="sm" inheritColor />
                  <span className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium">Prev</span>
                </button>

                <div className="flex items-center gap-[3px]">
                  {(() => {
                    const pages: (number | '…')[] = [];
                    if (totalPages <= 11) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      pages.push(1);
                      if (page > 4) pages.push('…');
                      for (let i = Math.max(2, page - 3); i <= Math.min(totalPages - 1, page + 3); i++) pages.push(i);
                      if (page < totalPages - 4) pages.push('…');
                      pages.push(totalPages);
                    }
                    return pages.map((p, i) =>
                      p === '…' ? (
                        <span key={`ellipsis-${i}`} className="w-8 h-8 flex items-center justify-center text-bg-v2-sm text-text-tertiary select-none">…</span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setPage(p as number)}
                          className={`w-8 h-8 rounded-bg-v2-md text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium border transition-colors
                            ${page === p
                              ? 'bg-bg-brand-solid text-text-white border-transparent'
                              : 'bg-bg-primary text-text-secondary border-border-primary hover:bg-bg-secondary hover:border-border-secondary'
                            }`}
                        >
                          {p}
                        </button>
                      )
                    );
                  })()}
                </div>

                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="flex items-center gap-bg-v2-xs h-8 px-bg-v2-sm rounded-bg-v2-md text-text-tertiary hover:text-text-secondary hover:bg-bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <span className="text-bg-v2-sm font-bg-v2-sans font-bg-v2-medium">Next</span>
                  <Icon name={IconName.ArrowRight} size="sm" inheritColor />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail — full area */}
        {selectedTicket && (
          <TicketDetailPanel
            ticket={selectedTicket}
            canEdit={canEdit}
            isLoadingDetail={isLoadingDetail}
            onClose={() => selectTicket(null)}
            onSave={handleSaveTicket}
            onDelete={handleDeleteTicket}
            assigneeOptions={assigneeOptions}
          />
        )}
      </div>

    </div>

    {/* ── Bulk Delete Confirmation Modal ── */}
    <Modal
      isOpen={showBulkDeleteConfirm}
      onClose={() => setShowBulkDeleteConfirm(false)}
      title="Delete Tickets"
      size="sm"
      onSubmit={handleBulkDelete}
      submitLabel={isDeleting ? 'Deleting...' : `Delete ${selectedAllPages ? totalTickets : selectedIds.size} ticket${(selectedAllPages ? totalTickets : selectedIds.size) !== 1 ? 's' : ''}`}
      submitVariant="outline"
      submitClassName="!text-text-error-primary !border-border-error hover:!bg-bg-error-primary hover:!text-text-error-primary"
      cancelLabel="Cancel"
    >
      <Text color="secondary">
        Are you sure you want to delete{' '}
        <strong>{selectedAllPages ? `all ${totalTickets}` : selectedIds.size}</strong>{' '}
        ticket{(selectedAllPages ? totalTickets : selectedIds.size) !== 1 ? 's' : ''}?
        {selectedAllPages && (
          <span className="block mt-bg-v2-xs text-text-tertiary">This includes tickets across all pages.</span>
        )}
        {' '}This action cannot be undone.
      </Text>
    </Modal>

    {/* ── Create Ticket Modal ── */}
    <Modal
      isOpen={showCreateModal}
      onClose={() => setShowCreateModal(false)}
      title="Create Ticket"
      size="lg"
      hideFooter
    >
      <TicketForm
        form={form}
        formError={formError}
        onChange={patchForm}
        onClearError={() => setFormError('')}
        assigneeOptions={assigneeOptions}
      />
      <ModalFooter
        submitLabel={isSubmitting ? 'Creating...' : 'Create Ticket'}
        onCancel={() => { if (!isSubmitting) setShowCreateModal(false); }}
        onSubmit={handleCreateTicket}
      />
    </Modal>

</>
  );
};

export default TicketsPage;
